import Phaser from 'phaser';
import { FONT_FAMILY, GAME_WIDTH, getGameHeight } from '../config/constants';
import { crispText } from './textUtils';
import { createButton } from './Button';
import { soundManager } from '../audio/SoundManager';
import { fadeToScene } from './sceneTransition';

const PANEL_WIDTH = 560;
const PANEL_X = (GAME_WIDTH - PANEL_WIDTH) / 2;
const OVERLAY_DEPTH = 200;

export interface PauseOverlayOptions {
  title?: string;
  showResume?: boolean;
  showMainMenu?: boolean;
  closeLabel?: string;
}

export class PauseOverlay {
  private container: Phaser.GameObjects.Container;
  private scene: Phaser.Scene;
  private soundLabel!: Phaser.GameObjects.Text;
  private soundToggleBg!: Phaser.GameObjects.Graphics;
  private onResume: () => void;
  private toggleY: number = 0;

  constructor(scene: Phaser.Scene, onResume: () => void, options?: PauseOverlayOptions) {
    this.scene = scene;
    this.onResume = onResume;
    this.container = scene.add.container(0, 0).setDepth(OVERLAY_DEPTH).setVisible(false).setAlpha(0);

    const title = options?.title ?? 'PAUSED';
    const showResume = options?.showResume !== false;
    const showMainMenu = options?.showMainMenu !== false;
    const closeLabel = options?.closeLabel;

    // Semi-transparent dark overlay
    const dimBg = scene.add.rectangle(GAME_WIDTH / 2, getGameHeight() / 2, GAME_WIDTH, getGameHeight(), 0x000000, 0.7);
    dimBg.setInteractive(); // Block clicks through to game
    this.container.add(dimBg);

    // Calculate panel height based on visible buttons
    let buttonCount = 0;
    if (showResume) buttonCount++;
    if (showMainMenu) buttonCount++;
    if (closeLabel) buttonCount++;
    const panelHeight = 260 + buttonCount * 110;
    const panelY = (getGameHeight() - panelHeight) / 2;

    // Panel background
    const panelGfx = scene.add.graphics();
    panelGfx.fillStyle(0x1a1a2e, 0.95);
    panelGfx.fillRoundedRect(PANEL_X, panelY, PANEL_WIDTH, panelHeight, 32);
    panelGfx.lineStyle(4, 0x4a4a7a, 0.6);
    panelGfx.strokeRoundedRect(PANEL_X, panelY, PANEL_WIDTH, panelHeight, 32);
    this.container.add(panelGfx);

    // Title
    const titleText = crispText(scene.add.text(GAME_WIDTH / 2, panelY + 72, title, {
      fontFamily: FONT_FAMILY,
      fontSize: '56px',
      color: '#ccccee',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    this.container.add(titleText);

    // Separator line
    const sep = scene.add.graphics();
    sep.lineStyle(2, 0x4a4a7a, 0.4);
    sep.lineBetween(PANEL_X + 48, panelY + 120, PANEL_X + PANEL_WIDTH - 48, panelY + 120);
    this.container.add(sep);

    // Sound toggle row
    this.toggleY = panelY + 190;
    this.createSoundToggle(this.toggleY);

    // Buttons
    let nextBtnY = panelY + 310;

    const btnW = PANEL_WIDTH - 96;
    const btnH = 80;

    if (showResume) {
      createButton(scene, { x: GAME_WIDTH / 2, y: nextBtnY, w: btnW, h: btnH, label: 'Resume', color: 0x2a4a2a, hoverColor: 0x2a4a2a, container: this.container, fontSize: '34px', textColor: '#88cc88', strokeColor: 0x3a6a3a, fillAlpha: 0.9, stopPropagation: true, onClick: () => {
        this.hide();
      } });
      nextBtnY += 110;
    }

    if (closeLabel) {
      createButton(scene, { x: GAME_WIDTH / 2, y: nextBtnY, w: btnW, h: btnH, label: closeLabel, color: 0x2a3a4a, hoverColor: 0x2a3a4a, container: this.container, fontSize: '34px', textColor: '#88aacc', strokeColor: 0x3a5a6a, fillAlpha: 0.9, stopPropagation: true, onClick: () => {
        this.hide();
      } });
      nextBtnY += 110;
    }

    if (showMainMenu) {
      createButton(scene, { x: GAME_WIDTH / 2, y: nextBtnY, w: btnW, h: btnH, label: 'Main Menu', color: 0x4a2a2a, hoverColor: 0x4a2a2a, container: this.container, fontSize: '34px', textColor: '#cc8888', strokeColor: 0x6a3a3a, fillAlpha: 0.9, stopPropagation: true, onClick: () => {
        fadeToScene(scene, () => {
          // Sleep GameScene to preserve state, then launch MenuScene
          scene.scene.sleep('GameScene');
          scene.scene.launch('MenuScene');
        });
      } });
    }
  }

  private createSoundToggle(y: number): void {
    const labelText = crispText(this.scene.add.text(PANEL_X + 64, y, 'Sound', {
      fontFamily: FONT_FAMILY,
      fontSize: '36px',
      color: '#aaaacc',
      fontStyle: 'bold',
    })).setOrigin(0, 0.5);
    this.container.add(labelText);

    // Toggle switch background
    const toggleX = PANEL_X + PANEL_WIDTH - 64 - 104;
    const toggleW = 104;
    const toggleH = 56;

    this.soundToggleBg = this.scene.add.graphics();
    this.container.add(this.soundToggleBg);

    // Toggle state label (ON/OFF)
    this.soundLabel = crispText(this.scene.add.text(PANEL_X + PANEL_WIDTH - 64, y, '', {
      fontFamily: FONT_FAMILY,
      fontSize: '26px',
      color: '#aaaacc',
      fontStyle: 'bold',
    })).setOrigin(1, 0.5);
    this.container.add(this.soundLabel);

    this.drawToggle(toggleX, y, toggleW, toggleH);

    // Hit area for toggle
    const hitArea = this.scene.add.rectangle(toggleX + toggleW / 2 + 20, y, toggleW + 80, toggleH + 20, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.container.add(hitArea);

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      soundManager.init();
      soundManager.toggleMute();
      this.drawToggle(toggleX, y, toggleW, toggleH);
    });
  }

  private drawToggle(x: number, y: number, w: number, h: number): void {
    const isOn = !soundManager.muted;
    this.soundToggleBg.clear();

    // Track
    this.soundToggleBg.fillStyle(isOn ? 0x44aa66 : 0x555577, 1);
    this.soundToggleBg.fillRoundedRect(x, y - h / 2, w, h, h / 2);

    // Knob
    const knobR = h / 2 - 6;
    const knobX = isOn ? x + w - knobR - 8 : x + knobR + 8;
    this.soundToggleBg.fillStyle(0xffffff, 0.95);
    this.soundToggleBg.fillCircle(knobX, y, knobR);

    this.soundLabel.setText(isOn ? 'ON' : 'OFF');
    this.soundLabel.setColor(isOn ? '#88cc88' : '#888899');
  }


  show(): void {
    this.drawToggle(
      PANEL_X + PANEL_WIDTH - 64 - 104,
      this.toggleY,
      104,
      56,
    );
    this.container.setVisible(true);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: 150,
      ease: 'Sine.easeOut',
    });
  }

  hide(): void {
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 150,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.container.setVisible(false);
        this.onResume();
      },
    });
  }

  get visible(): boolean {
    return this.container.visible;
  }
}
